import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import SiteFooter from "@/components/SiteFooter";
import { PauseCircle, Menu, X } from "lucide-react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const tabs = [
    { to: "/dashboard", label: "Feed" },
    { to: "/sent", label: "Sent" },
    { to: "/profile", label: "Profile" },
    { to: "/subscription", label: "Plan" },
  ];

  const trialDays = user?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000))
    : 0;
  const subActive = user?.subscription_ends_at && new Date(user.subscription_ends_at) > new Date();
  const grantedFree = !!user?.granted_access;
  const isAdmin = !!user?.is_admin;
  const paused = !!user?.paused;

  const ALLOWED_WHEN_PAUSED = ["/profile", "/sent", "/applications", "/admin"];
  const pathBlocked = paused && !ALLOWED_WHEN_PAUSED.some((p) => location.pathname.startsWith(p));

  const renderStatus = () => {
    if (isAdmin)     return <span className="text-primary"      data-testid="status-admin">ADMIN · ∞</span>;
    if (paused)      return <span className="text-accent-amber" data-testid="status-paused">PAUSED</span>;
    if (grantedFree) return <span className="text-accent-green" data-testid="status-granted">GRANTED · ∞</span>;
    if (subActive)   return <span className="text-accent-green">ACTIVE</span>;
    if (trialDays > 0) return <span className="text-accent-amber">TRIAL · {trialDays}D</span>;
    return <span className="text-destructive">EXPIRED</span>;
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 glass-strong border-b border-foreground/30">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 h-14 md:h-16 flex items-center justify-between gap-2">
          <Link to="/dashboard" data-testid="layout-logo-link" className="flex-shrink-0">
            <Logo />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-0 lg:gap-1" data-testid="layout-nav">
            {tabs.map((t) => {
              const active = location.pathname.startsWith(t.to);
              return (
                <Link
                  key={t.to}
                  to={t.to}
                  data-testid={`nav-${t.label.toLowerCase()}`}
                  className={`px-3 lg:px-4 py-2 text-xs uppercase tracking-[0.2em] lg:tracking-[0.25em] transition-colors ${
                    active ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  [{t.label}]
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden lg:flex items-center gap-2 text-[10px] uppercase tracking-[0.2em]">
              <span className="text-muted-foreground">STATUS:</span>
              {renderStatus()}
            </div>
            <ThemeToggle />
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); navigate("/"); }}
              className="btn-ghost hidden md:inline-flex"
            >
              Logout
            </button>
            {/* Mobile hamburger */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              className="md:hidden p-2 -mr-2 text-foreground"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="md:hidden border-t border-foreground/30 bg-background" data-testid="mobile-menu-drawer">
            <nav className="px-5 py-3 flex flex-col">
              {tabs.map((t) => {
                const active = location.pathname.startsWith(t.to);
                return (
                  <Link
                    key={t.to}
                    to={t.to}
                    onClick={closeMobile}
                    data-testid={`mobile-nav-${t.label.toLowerCase()}`}
                    className={`py-3 text-xs uppercase tracking-[0.25em] border-b border-foreground/10 last:border-b-0 ${
                      active ? "text-primary" : "text-foreground/80"
                    }`}
                  >
                    [{t.label}]
                  </Link>
                );
              })}
              <div className="flex items-center justify-between pt-4 pb-2">
                <div className="text-[10px] uppercase tracking-[0.2em] flex items-center gap-2">
                  <span className="text-muted-foreground">STATUS:</span>
                  {renderStatus()}
                </div>
                <button
                  data-testid="mobile-logout-btn"
                  onClick={() => { closeMobile(); logout(); navigate("/"); }}
                  className="btn-ghost"
                >
                  Logout
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">
        {pathBlocked ? <PausedBlock /> : children}
      </main>

      <footer>
        <SiteFooter variant="app" />
      </footer>
    </div>
  );
}

function PausedBlock() {
  return (
    <div className="max-w-2xl mx-auto px-5 md:px-12 py-12 sm:py-20" data-testid="paused-block">
      <div className="panel p-6 sm:p-10 text-center corner-marks relative border-accent-amber/40" style={{ borderColor: "hsl(var(--accent-amber) / 0.4)" }}>
        <PauseCircle className="w-10 h-10 text-accent-amber mx-auto mb-6" strokeWidth={1.5} />
        <div className="terminal-label text-accent-amber mb-3">[ ACCOUNT.PAUSED ]</div>
        <h1 className="font-display text-xl sm:text-2xl uppercase font-bold mb-3">Your account is paused</h1>
        <p className="text-sm text-muted-foreground mb-2 max-w-md mx-auto leading-relaxed">
          The feed, AI generation, and sending are frozen. Your profile and any portfolio URLs you've already shared are still live.
        </p>
        <p className="text-xs text-muted-foreground mb-8">Head to <span className="font-mono text-foreground">Profile</span> to resume your account whenever you're ready.</p>
        <Link to="/profile" data-testid="paused-go-to-profile" className="btn-primary">Go to profile →</Link>
      </div>
    </div>
  );
}
